/**
 * Builds a compact printable HTML document from a rendered ticket or report root element.
 *
 * The builder clones the print tree, strips Angular runtime artifacts (encapsulation markers,
 * dev-mode reflection, comment placeholders) and inlines a minimal owned stylesheet. The resulting
 * document is independent of the application's global CSS so it can be consumed by browser print
 * preview, server renderers (for example `dompdf`) or ESC/POS HTML bridges without dragging the
 * full app stylesheet payload.
 *
 * The builder never inspects the host's own markup to decide how to serialize it: by default it
 * clones `nativeElement` as-is (WYSIWYG with the on-screen preview). Callers that need the legacy
 * ticket contract (content normalized under a `.upp-ticket-print` wrapper) opt in explicitly via
 * the `wrapperClass` parameter of {@link BuildHtml}.
 */
export class htmlBuilder {
    /**
     * HTML attributes preserved on the cloned print tree. Anything outside this whitelist is dropped
     * during sanitization. The list is intentionally tiny: the printable ticket is expressed with
     * tables, classes and inline styles only.
     */
    private static readonly ALLOWED_ATTRS = new Set<string>([
        'class',
        'style',
        'src',
        'alt',
        'colspan',
        'rowspan',
        'align',
        'valign',
        'width',
        'height',
        'href',
    ]);

    /**
     * Attribute name prefixes added by Angular at runtime that must not leak into the printable
     * document (encapsulation markers, dev-mode reflection, structural placeholders, etc.).
     */
    private static readonly STRIP_ATTR_PREFIXES = [
        '_ngcontent-',
        '_nghost-',
        'ng-reflect-',
    ];

    /**
     * Exact attribute names emitted by Angular or by E2E tooling that have no purpose in print output.
     */
    private static readonly STRIP_EXACT_ATTRS = new Set<string>([
        'ng-version',
        'ng-star-inserted',
        'uppdataid',
        'data-id',
    ]);

    /**
     * @param nativeElement Rendered ticket or report host element to serialize.
     */
    constructor(
        private readonly nativeElement: HTMLElement,
    ) {}

    /**
     * Complete printable HTML document (`<!doctype html>...`) ready to be dispatched to print
     * backends or opened in a browser print window.
     *
     * @param style Stylesheet inlined in the document `<head>`.
     * @param wrapperClass When provided, `nativeElement`'s children are cloned into a `<div>` with
     *   this class instead of `nativeElement` itself (ticket / audit contract, see class docs).
     *   Omit it to serialize `nativeElement` faithfully.
     */
    async BuildHtml(style: string, wrapperClass: string | null = null): Promise<string> {
        const bodyMarkup = this._BuildBodyMarkup(wrapperClass);

        return [
            '<!doctype html>',
            '<html>',
            '<head>',
            '<meta charset="utf-8" />',
            '<title>Ticket</title>',
            `<style>${style}</style>`,
            '</head>',
            '<body>',
            bodyMarkup,
            '</body>',
            '</html>',
        ].join('');
    }

    /*****************************/
    /* PRIVATE METHODS           */
    /*****************************/

    /**
     * Serializes the print body: faithful clone of `nativeElement` by default, or its children
     * normalized under `wrapperClass` when the caller opts into the ticket contract.
     */
    private _BuildBodyMarkup(wrapperClass: string | null): string {
        if (!wrapperClass) {
            const clone = this.nativeElement.cloneNode(true) as Element;
            this._SanitizeNode(clone);
            return (clone as HTMLElement).outerHTML;
        }

        const doc = this.nativeElement.ownerDocument || document;
        const wrapper = doc.createElement('div');
        wrapper.setAttribute('class', wrapperClass);

        for (const child of Array.from(this.nativeElement.childNodes)) {
            if (child.nodeType !== Node.ELEMENT_NODE) {
                continue;
            }

            const clone = child.cloneNode(true) as Element;
            this._SanitizeNode(clone);
            wrapper.appendChild(clone);
        }

        return wrapper.outerHTML;
    }

    private _SanitizeNode(node: Node): void {
        if (node.nodeType === Node.COMMENT_NODE) {
            node.parentNode?.removeChild(node);
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        const element = node as Element;
        for (const attr of Array.from(element.attributes)) {
            if (this._ShouldStripAttr(attr.name)) {
                element.removeAttribute(attr.name);
            }
        }

        for (const child of Array.from(element.childNodes)) {
            this._SanitizeNode(child);
        }
    }

    private _ShouldStripAttr(name: string): boolean {
        const lower = name.toLowerCase();
        if (htmlBuilder.ALLOWED_ATTRS.has(lower)) {
            return false;
        }
        if (htmlBuilder.STRIP_EXACT_ATTRS.has(lower)) {
            return true;
        }
        for (const prefix of htmlBuilder.STRIP_ATTR_PREFIXES) {
            if (lower.startsWith(prefix)) {
                return true;
            }
        }
        return true;
    }

}
